// KEYFRAME, ported from internal/wire/keyframe.go.
//
// The recovery path of SPEC §3, in the sense a video codec means it: run-length
// encoded over (countryId, hp, flags) and then gzipped, encoded ONCE and fanned
// out to every client identically.
//
//	u8  scope            0 = one chunk, 1 = the whole board
//	u16 chunkId          the chunk, or 0 for the board scope
//	u32 pixelCount       PixelsPerChunk or NumPixels
//	u32 payloadLen       gzipped byte count that follows
//	[]  gzip(payload)
//
// and the payload, once inflated:
//
//	u32 runCount
//	runCount x (u16 countryId | u32 hp | u8 flags | u16 runLen)
//
// Flags are in the RLE key, not just the value: a recovered client that lost them
// would render fortresses as plain tiles.
//
// The decode target is three typed arrays, never an object per pixel. A board
// keyframe covers 102,400 pixels; 102,400 little objects is the difference
// between a client that repaints smoothly and one that stutters for a second
// every ten.

import { Reader, type Bytes } from './bytes';
import { ClassKeyframe } from './classes';
import { NumPixels, PixelsPerChunk, validChunk, type ChunkID, type TickNo } from './core';
import {
  errChunkID,
  errGzip,
  errPayloadSize,
  errPixelCount,
  errRunOverflow,
  errScope,
  errTruncated,
  errZeroRun,
} from './errors';

export const ScopeChunk = 0;
export const ScopeBoard = 1;
export type Scope = typeof ScopeChunk | typeof ScopeBoard;

export function scopeName(s: number): string {
  return s === ScopeBoard ? 'board' : 'chunk';
}

/** The encoded width of one run — the same nine bytes as a delta record, by coincidence of field widths. */
export const RLERunSize = 2 + 4 + 1 + 2;

/** What the u16 length field holds. Longer runs are split and the decoder concatenates them without noticing. */
export const MaxRunLen = 0xffff;

/**
 * What a gzip stream is allowed to inflate to. A keyframe is server-authored, but
 * this decoder runs on whatever the socket hands it, so it refuses to be a
 * decompression bomb.
 */
export const MaxPayload = 4 + NumPixels * RLERunSize;

/** The three parallel arrays a decoded board lives in. */
export interface PixelArrays {
  country: Uint16Array;
  hp: Uint32Array;
  flags: Uint8Array;
}

export interface Keyframe extends PixelArrays {
  tick: TickNo;
  scope: Scope;
  /** Meaningful only for ScopeChunk. */
  chunk: ChunkID;
  /**
   * Indexed by chunk offset for ScopeChunk (the same offset the wire carries) and
   * by PixelID for ScopeBoard.
   */
  pixelCount: number;
}

/**
 * Decodes a KEYFRAME. Async because gunzip in the browser is
 * `DecompressionStream('gzip')`, which is a stream.
 *
 * A caller that dispatches frames off a socket MUST keep the queue ordered
 * around this await — a TICK applied while its keyframe is still inflating is
 * read as a gap on a board that was about to be replaced.
 */
export async function decodeKeyframe(b: Uint8Array): Promise<Keyframe> {
  const r = new Reader(b);
  const tick = r.header(ClassKeyframe);
  const scope = r.u8();
  let chunk = r.u16();
  const pixelCount = r.u32();
  const zlen = r.u32();
  const z = r.copy(zlen);
  r.done();

  switch (scope) {
    case ScopeChunk:
      if (!validChunk(chunk)) throw errChunkID();
      if (pixelCount !== PixelsPerChunk) throw errPixelCount();
      break;
    case ScopeBoard:
      if (pixelCount !== NumPixels) throw errPixelCount();
      chunk = 0;
      break;
    default:
      throw errScope();
  }

  const payload = await inflate(z);
  const pr = new Reader(payload);
  const runCount = pr.u32();
  // A run cannot be shorter than RLERunSize, so a count that could not fit in
  // what is left is rejected before anything is allocated for it.
  if (runCount * RLERunSize > pr.remaining) throw errTruncated();

  // Read every run before expanding any of them, and check the trailing bytes in
  // between. That is the order internal/wire decodes in — runs are parsed, the
  // payload reader is closed out, and only then does RunLengthDecode judge the
  // run contents — so a payload that is both malformed AND over-long reports the
  // same error on both sides of the wire.
  const runCountry = new Uint16Array(runCount);
  const runHP = new Uint32Array(runCount);
  const runFlags = new Uint8Array(runCount);
  const runLen = new Uint16Array(runCount);
  for (let i = 0; i < runCount; i++) {
    runCountry[i] = pr.u16();
    runHP[i] = pr.u32();
    runFlags[i] = pr.u8();
    runLen[i] = pr.u16();
  }
  pr.done();

  const country = new Uint16Array(pixelCount);
  const hp = new Uint32Array(pixelCount);
  const flags = new Uint8Array(pixelCount);
  let at = 0;
  for (let i = 0; i < runCount; i++) {
    const len = runLen[i]!;
    if (len === 0) throw errZeroRun();
    if (at + len > pixelCount) throw errRunOverflow();
    const end = at + len;
    country.fill(runCountry[i]!, at, end);
    hp.fill(runHP[i]!, at, end);
    flags.fill(runFlags[i]!, at, end);
    at = end;
  }
  if (at !== pixelCount) throw errPixelCount();

  return { tick, scope: scope as Scope, chunk, pixelCount, country, hp, flags };
}

/**
 * Gunzip with the same ceiling internal/wire enforces, checked as the stream is
 * consumed rather than after — a bomb that would expand to a gigabyte must not
 * be allowed to allocate a gigabyte first.
 */
export async function inflate(z: Bytes): Promise<Uint8Array> {
  const stream = new DecompressionStream('gzip');
  const writer = stream.writable.getWriter();
  // The write is not awaited before reading starts: a gzip stream larger than the
  // transform's internal buffer deadlocks if the producer is drained first.
  const written = writer.write(z).then(() => writer.close());
  // The stream reports a corrupt payload on both halves; the reader's rejection is
  // the one that carries it, so the writer's is neutralised rather than left to
  // surface as an unhandled rejection.
  written.catch(() => {});

  const reader = stream.readable.getReader();
  const parts: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > MaxPayload) {
        await reader.cancel().catch(() => {});
        throw errPayloadSize();
      }
      parts.push(value);
    }
  } catch (e) {
    if (e instanceof Error && e.name === 'WireError') throw e;
    throw errGzip();
  }
  if (parts.length === 1) return parts[0]!;
  const out = new Uint8Array(total);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.byteLength;
  }
  return out;
}
